'use client';

import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import * as React from 'react';

import { Calendar } from '@/components/ui/calendar';
import { Popover } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { FieldValues, Path, PathValue, UseFormSetValue } from 'react-hook-form';

interface DatePickerInputProps<T extends FieldValues> {
  disabled?: boolean;
  error?: string;
  placeholder: string;
  setValue?: UseFormSetValue<T>;
  name: string;
  value?: Date;
  // eslint-disable-next-line no-unused-vars
  onDateChange?: (date: Date) => void;
  className?: string;
}

export function DatePickerInput<T extends FieldValues>({
  disabled,
  placeholder,
  error,
  setValue,
  name,
  value,
  onDateChange,
  className,
}: DatePickerInputProps<T>) {
  const [date, setDate] = React.useState<Date | undefined>(value);

  React.useEffect(() => {
    setDate(value);
  }, [value]);

  return (
    <div
      className={cn(
        'self-stretch inline-flex flex-col justify-center items-center gap-1.5',
        className,
      )}
    >
      <div
        className={cn(
          'input-shadow self-stretch rounded-lg outline-1 outline-offset-[-1px] inline-flex justify-start items-center gap-2 overflow-hidden input-shadow transition-colors',
          disabled ? 'bg-gray-50' : 'bg-white',
          error
            ? 'input-error outline-error-300 focus-within:outline-error-300'
            : 'outline-gray-300 focus-within:outline-blue-300',
        )}
      >
        <Popover
          trigger={
            <div
              className={cn(
                'px-3.5 py-3 flex flex-row w-full justify-start items-center gap-2 overflow-hidden text-sm group cursor-pointer h-10',
                !date && 'text-gray-500',
                disabled && 'cursor-not-allowed opacity-50',
              )}
              role="button"
              tabIndex={disabled ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                }
              }}
            >
              {date ? format(date, 'PPP', { locale: es }) : placeholder}
            </div>
          }
          content={
            <Calendar
              mode="single"
              selected={date}
              onSelect={(date) => {
                setDate(date);
                setValue?.(name as Path<T>, date as PathValue<T, Path<T>>);
                onDateChange?.(date as Date);
              }}
            />
          }
        />
      </div>
      {error && (
        <div className="self-stretch justify-start text-error-500 text-xs font-normal leading-none">
          {error}
        </div>
      )}
    </div>
  );
}
